import { CompositeLayer, PickingInfo } from "@deck.gl/core"
import { PathStyleExtension } from "@deck.gl/extensions"
import { PathLayer, PolygonLayer } from "@deck.gl/layers"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { selectCurrentGeometryToolType } from "../geometry/SharedGeometrySlice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { deckglEventEmitter } from "features/map/MapElements"
import { setHoveredBoundingBoxId } from "features/map/layers/slice"
// deckgl dependency, I don't want to control the version
// oxlint-disable-next-line nova/no-implicit-dependencies
import { MjolnirGestureEvent } from "mjolnir.js"
import { getGlobalTerrainData, useGlobalTerrainData } from "features/map/GlobalTerrainManager"
import {
    clearSelection,
    selectLastSelectedLayerId,
    selectLockedLayersMap,
    selectSingleLayer,
} from "panels/content/side-panel/slice.new"
import { getAncestors } from "panels/content/side-panel/utils"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getBoundingBoxDataProviders } from "plugins/plugin-registry"
import { useRouter } from "pages"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useLocation } from "react-router"

export type BoundingBoxLayerData = {
    id: string
    polygon: [number, number][]
    path: [number, number][]
    area: number
}

let currentListener = undefined as ((_: PickingInfo, event: MouseEvent) => void) | undefined

const useBoundingBoxLayerData = () => {
    const data = getBoundingBoxDataProviders().map((e) => e.useBoundingBoxData())
    return data
}

export const useBoundingBoxLayerLabelData = () => {
    const data = getBoundingBoxDataProviders().reduce((acc, e) => {
        if (!e.showLabel) return acc
        acc.push(e.useBoundingBoxData())
        return acc
    }, [] as BoundingBoxLayerData[][])
    return data
}

export const useBoundingBoxLayer = () => {
    const { isEditor } = useWorkspacePermissions()
    const snapshotIsOpen = useAppSelector(selectSnapshotOpen())
    const selectedId = useAppSelector(selectLastSelectedLayerId)
    const currentGeometryType = useAppSelector(selectCurrentGeometryToolType)
    const data = useBoundingBoxLayerData()
    const lockedLayersById = useAppSelector(selectLockedLayersMap)
    const { terrainState: terrainIsActive } = useGlobalTerrainData(["terrainState"])
    const router = useRouter("/")
    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId())
    const activeProjectId = useAppSelector(selectActivatedFireId)
    const projectViewUrl = router.preview({
        path: "/workspace/:workspaceId/project/:projectId",
        params: { workspaceId: activeWorkspaceId || "", projectId: activeProjectId || "" },
    })
    const location = useLocation()
    const isInProject = location.pathname === projectViewUrl

    if (snapshotIsOpen || !isEditor || !!currentGeometryType || !isInProject) {
        if (currentListener) {
            deckglEventEmitter.off("click", currentListener)
            currentListener = undefined
        }
        return
    }
    return new BoundingBoxLayer({ data, selectedId, terrainIsActive, lockedLayersById })
}

class BoundingBoxLayer extends CompositeLayer<{
    data: BoundingBoxLayerData[][]
    lockedLayersById: Record<string, boolean>
    selectedId?: string
    terrainIsActive?: boolean
}> {
    static layerName = "BoundingBoxLayer"
    static override get componentName() {
        return "BoundingBoxLayer"
    }
    state = { data: [] } as {
        data: BoundingBoxLayerData[]
        hoveredId?: string
        selectedId?: string
    }
    updateState({ changeFlags }: { changeFlags: Record<string, unknown> }) {
        const data = this.props.data
        const dataChanged = changeFlags.dataChanged
        const selectedIdChanged =
            changeFlags.propsChanged && this.state.selectedId !== this.props.selectedId
        if (dataChanged) {
            const filteredAndSortedData = data
                .flat()
                .sort((a, b) => (b.area || 0) - (a.area || 0))
                .filter((data) => {
                    const state = store.getState()
                    const entityMap = selectAllEntitiesMap(state)
                    return (
                        !this.props.lockedLayersById[data.id] &&
                        !getAncestors(data.id, entityMap).find((ancestor) => ancestor.locked)
                    )
                })
            this.setState({ data: filteredAndSortedData })
        }
        if (selectedIdChanged) {
            this.setState({ selectedId: this.props.selectedId })
            if (this.state.selectedId) {
                this.setListener()
            } else if (currentListener) {
                deckglEventEmitter.off("click", currentListener)
                currentListener = undefined
            }
        }
    }

    setListener(triggeringEvent?: MouseEvent | MjolnirGestureEvent) {
        if (currentListener) deckglEventEmitter.off("click", currentListener)
        const onOffClick = (_: PickingInfo, event: MouseEvent) => {
            if (event === triggeringEvent) return
            this.setState({ selectedId: undefined })
            store.dispatch(clearSelection())
            deckglEventEmitter.off("click", onOffClick)
        }
        currentListener = onOffClick
        deckglEventEmitter.on("click", onOffClick)
    }

    onClick(info: PickingInfo, event: MjolnirGestureEvent) {
        if (event.rightButton) return false
        if (this.state.selectedId !== info.object.id) {
            store.dispatch(selectSingleLayer({ id: info.object.id }))
            this.setState({ selectedId: info.object.id })
            this.setListener(event)
        }
        return false
    }

    onHover = (info: PickingInfo) => {
        if (info.object?.id !== this.state.hoveredId) {
            store.dispatch(setHoveredBoundingBoxId(info.object?.id))
            this.setState({ hoveredId: info.object?.id })
        }
        return true
    }

    isSelected(id: string) {
        return this.state.selectedId === id
    }

    isHovered(id: string) {
        return this.state.hoveredId === id
    }

    renderLayers() {
        // Pixel ratio is 1 for normal screens and 2 for retina dsiplays (macbooks)
        // we can display a 0.5 width line on retina displays, but it glitches on normal
        const pixelRatio = Math.min(window?.devicePixelRatio ?? 1, 3) // clamped [1, 3] for common ratios
        const minVisibleWidth = 1 / pixelRatio // a single pixel on the target screen
        const getWidthForId = (id: string) => {
            const [selectedWidth, hoveredWidth, defaultWidth] = this.props.terrainIsActive
                ? [1.5, 1, 0]
                : [1, minVisibleWidth, 0]
            return this.isSelected(id)
                ? selectedWidth
                : this.isHovered(id)
                  ? hoveredWidth
                  : defaultWidth
        }
        return [
            new PolygonLayer({
                id: "bbox-cursor-hitbox",
                data: this.state.data,
                getPolygon: (d) => d.polygon,
                pickable: true,
                getFillColor: [0, 0, 0, 0],
                getLineColor: [0, 0, 0, 0],
                lineWidthMinPixels: 1,
                lineWidthMaxPixels: 1,
                extensions: [...getGlobalTerrainData().extensions],
            }),
            new PathLayer({
                id: "bbox-outline-white",
                data: this.state.data,
                updateTriggers: {
                    getWidth: [
                        this.state.selectedId,
                        this.state.hoveredId,
                        this.props.terrainIsActive,
                    ],
                },
                getPath: (d) => d.path,
                getColor: [255, 255, 255, 255],
                getWidth: (d) => getWidthForId(d.id),
                extensions: [...getGlobalTerrainData().extensions],
                widthUnits: "pixels",
                pickable: true,
                visible: !!this.state.selectedId || !!this.state.hoveredId,
            }),
            new PathLayer({
                id: "bbox-outline-blue",
                data: this.state.data,
                updateTriggers: {
                    getWidth: [
                        this.state.selectedId,
                        this.state.hoveredId,
                        this.props.terrainIsActive,
                    ],
                },
                getPath: (d) => d.path,
                getColor: [44, 86, 237, 255],
                getWidth: (d: { id: string }) => getWidthForId(d.id),
                widthUnits: "pixels",
                extensions: [
                    ...getGlobalTerrainData().extensions,
                    new PathStyleExtension({ dash: true }),
                ],
                getDashArray: [10, 10],
                pickable: true,
                visible: !!this.state.selectedId || !!this.state.hoveredId,
            }),
        ]
    }
}
